import { ListItemIcon, ListItemText, MenuItem } from "@mui/material";
import { useState } from "react";

import AppIcon from "@/common/components/Icons/AppIcon";
import { EntityHelper } from "@/common/helpers/entity";
import { StringHelper } from "@/common/helpers/string";
import { useApiRequest } from "@/common/hooks/api/useApiRequest";
import { useDisplayProps } from "@/common/hooks/useDisplayProps";
import { WheelOperationDtoAll } from "@/common/mapping/mappers/wheelOperation";
import { ApiEnumName, enumService } from "@/common/services/enum";
import { apiClient } from "@/core/api/ApiClient";
import { WheelOperationDto, WheelOperationReferenceDto } from "@/core/api/generated/v1";

import MenuWithTrigger from "../../Menu/MenuWithTrigger";
import { getVehicleInlineTitleShort } from "../Vehicle/VehicleInline";
import BaseEntityInline, {
  BaseEntityInlineInheritableProps,
} from "../components/EntityInline/BaseEntityInline";
import { EntityInlineActionIcon } from "../components/EntityInline/EntityInlineActionIcon";
import { EntityInlineActionIconButton } from "../components/EntityInline/EntityInlineActionIconButton";
import { EntityInlineActions } from "../components/EntityInline/EntityInlineActions";
import { WheelOperationViewModal } from "./View/WheelOperationViewModal";

const defaultDisplayProps = { vehicle: true };
type DisplayProps = typeof defaultDisplayProps;

export const getWheelOperationInlineTitle = (
  data: Nil<WheelOperationDtoAll>,
  displayProps?: DisplayProps,
) =>
  data
    ? StringHelper.join(
        [
          EntityHelper.getNumberTitle(data),
          enumService.getEnumValueName(ApiEnumName.WheelOperationStage, data.stageHistory?.stage),
          displayProps?.vehicle && data.vehicle
            ? getVehicleInlineTitleShort(data.vehicle)
            : undefined,
        ],
        ", ",
        {
          skipEmpty: true,
        },
      )
    : undefined;

export interface WheelOperationInlineProps extends BaseEntityInlineInheritableProps {
  entity: WheelOperationDto | WheelOperationReferenceDto | null | undefined;
  entityId?: string | null;
  displayProps?: Partial<DisplayProps>;
}

export default function WheelOperationInline({
  entity,
  entityId,
  displayProps,
  fetch = true,
  withActions,
  withMenu,
  viewProps,
  ...otherProps
}: WheelOperationInlineProps) {
  const displayPropsComputed = useDisplayProps([defaultDisplayProps, displayProps]);

  const [isViewModalOpen, setIsViewModalOpen] = useState(false);

  const request = useApiRequest(
    apiClient.wheelOperationsApi.apiV1WheelOperationsWheelOperationIdGet,
    {
      nexusOpsTenant: EMPTY_TENANT_IDENTIFIER,
      wheelOperationId: entityId!,
    },
    {
      deps: [entityId],
      skip: !fetch || !entityId || !!entity,
    },
  );
  entity = request?.data || entity;

  const entityIdComputed = entity?.id || entityId;
  const full = entity as WheelOperationDto;
  // const ref = entity as WheelOperationReferenceDto;

  return (
    <>
      <BaseEntityInline
        entity={entity}
        entityId={entityId}
        request={fetch ? request : undefined}
        icon={<AppIcon of='wheelOperation' inText />}
        content={getWheelOperationInlineTitle(full, displayPropsComputed) || "Wheel Operation"}
        withActions={withActions}
        withMenu={withMenu}
        actions={
          !withActions ? undefined : (
            <EntityInlineActions>
              <EntityInlineActionIconButton
                onClick={() => setIsViewModalOpen(true)}
                tooltipProps={{ title: "Quick view" }}
              >
                <EntityInlineActionIcon of='quickView' />
              </EntityInlineActionIconButton>
            </EntityInlineActions>
          )
        }
        menu={
          !withMenu ? undefined : (
            <MenuWithTrigger
              withAuthCloseOnClick
              trigger={
                <EntityInlineActionIconButton>
                  <EntityInlineActionIcon of='moreVert' />
                </EntityInlineActionIconButton>
              }
            >
              <MenuItem dense onClick={() => setIsViewModalOpen(true)}>
                <ListItemIcon>
                  <AppIcon of='quickView' fontSize='small' />
                </ListItemIcon>
                <ListItemText>Quick view</ListItemText>
              </MenuItem>
            </MenuWithTrigger>
          )
        }
        {...otherProps}
        tooltipTitle={otherProps?.tooltipTitle || "Wheel operation"}
      />

      {(withActions || withMenu) && (
        <>
          {/* View modal */}
          {entityIdComputed && (
            <WheelOperationViewModal
              open={isViewModalOpen}
              onClose={() => setIsViewModalOpen(false)}
              viewProps={{
                wheelOperationId: entityIdComputed,
                wheelOperation: EntityHelper.nullIfNotFull(entity),
                onUpdate: viewProps?.onUpdate || null,
                onDelete: viewProps?.onDelete || null,
              }}
            />
          )}
        </>
      )}
    </>
  );
}
